/**
 * components/ChatbotDrawer.js — Phase 4 Week 2
 *
 * The main chatbot UI container — a MUI Drawer that slides in from the right.
 *
 * Features:
 *  • 420px wide on desktop, full-screen on mobile (matches md breakpoint)
 *  • Header: title, clear session button, close button
 *  • ChatSuggestions chips shown until first message is sent
 *  • ChatMessageList — scrollable conversation history
 *  • ChatInputBar — sticky at bottom
 *  • Global error banner for send-level failures
 *  • Dark mode via MUI palette tokens throughout
 *  • Session-level send error shown as Alert above input bar
 *
 * Props:
 *  open        boolean    — controls drawer visibility
 *  onClose     function   — called when close button / backdrop clicked
 *  userRoles   string[]   — from AuthContext user.roles (for suggestions)
 */
import React, { useEffect, useRef } from 'react';
import {
  Alert, Box, Divider, Drawer, IconButton,
  Tooltip, Typography, useMediaQuery, useTheme,
} from '@mui/material';
import { Close, DeleteOutline, SmartToy, DashboardCustomize, Stop } from '@mui/icons-material';
import { useChat }           from '../hooks/useChat';
import ChatMessageList       from './ChatMessageList';
import ChatInputBar          from './ChatInputBar';
import ChatSuggestions       from './ChatSuggestions';

const DRAWER_WIDTH = 420;

export default function ChatbotDrawer({ open, onClose, userRoles = [], initialMessage = null }) {
  const theme   = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const {
    messages,
    isLoading,
    isStreaming,
    stompStatus,
    error,
    sendMessage,
    cancelStreaming,
    clearSession,
  } = useChat({ open });

  // Auto-send initialMessage once when the drawer opens and STOMP is ready
  const pendingSentRef = useRef(false);
  useEffect(() => {
    if (!open) { pendingSentRef.current = false; }
  }, [open]);
  useEffect(() => {
    if (open && initialMessage && !pendingSentRef.current && stompStatus === 'connected' && !isLoading && !isStreaming) {
      pendingSentRef.current = true;
      sendMessage(initialMessage);
    }
  }, [open, initialMessage, stompStatus, isLoading, isStreaming, sendMessage]);

  const hasMessages     = messages.length > 0;
  const inputDisabled   = isLoading || isStreaming;

  const handleSuggestion = (text) => sendMessage(text);

  const handleClear = async () => {
    await clearSession();
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={(event, reason) => {
        // Only allow closing via the close button, not backdrop click or Escape key
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
        onClose();
      }}
      variant="temporary"
      ModalProps={{ keepMounted: true }}   // keep WebSocket subscription alive
      sx={{
        '& .MuiDrawer-paper': {
          width:     isMobile ? '100vw' : DRAWER_WIDTH,
          maxWidth:  '100vw',
          display:   'flex',
          flexDirection: 'column',
          bgcolor:   'background.paper',
          boxShadow: 8,
        },
        // Sit above the dashboard FAB (z-index 1300) but below MUI Modal default
        zIndex: (t) => t.zIndex.drawer + 2,
      }}
    >
      {/* ── Header ──────────────────────────────────────────────────────────── */}
      <Box
        sx={{
          display: 'flex', alignItems: 'center', gap: 1.5,
          px: 2, py: 1.5,
          borderBottom: '1px solid', borderColor: 'divider',
          flexShrink: 0,
        }}
      >
        {/* Icon */}
        <Box
          sx={{
            width: 34, height: 34, borderRadius: '10px', flexShrink: 0,
            background: 'linear-gradient(135deg, #1565C0 0%, #42A5F5 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          {/*<SmartToy sx={{ fontSize: 18, color: '#fff' }} />*/}
          <DashboardCustomize sx={{ fontSize: 18, color: '#fff' }} />
        </Box>

        {/* Title */}
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle1" fontWeight={700} lineHeight={1.2}>
            HRMS Assistant
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{
              width: 7, height: 7, borderRadius: '50%', flexShrink: 0,
              bgcolor:
                stompStatus === 'connected'  ? '#4CAF50' :
                stompStatus === 'error'      ? '#F44336' : '#FF9800',
              boxShadow:
                stompStatus === 'connected'  ? '0 0 0 2px rgba(76,175,80,0.25)' :
                stompStatus === 'error'      ? '0 0 0 2px rgba(244,67,54,0.25)' :
                                               '0 0 0 2px rgba(255,152,0,0.25)',
            }} />
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
              {stompStatus === 'connected'
                ? 'Ask anything about your HR data'
                : stompStatus === 'error'
                  ? 'Connection error — retrying…'
                  : 'Connecting…'}
            </Typography>
          </Box>
        </Box>

        {/* Actions */}

        {/* Stop button — only visible while the assistant is thinking or streaming */}
        {(isLoading || isStreaming) && (
          <Tooltip title="Stop response">
            <IconButton
              size="small"
              onClick={cancelStreaming}
              sx={{
                color: 'error.main',
                bgcolor: 'error.light',
                opacity: 0.85,
                '&:hover': { opacity: 1, bgcolor: 'error.light' },
                width: 28, height: 28,
              }}
            >
              <Stop fontSize="small" />
            </IconButton>
          </Tooltip>
        )}

        <Tooltip title="Clear conversation">
          <span>
            <IconButton
              size="small"
              onClick={handleClear}
              disabled={!hasMessages || inputDisabled}
              sx={{ color: 'text.secondary', '&:hover': { color: 'error.main' } }}
            >
              <DeleteOutline fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>

        <Tooltip title="Close">
          <IconButton size="small" onClick={onClose} sx={{ color: 'text.secondary' }}>
            <Close fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {/* ── Message list ────────────────────────────────────────────────────── */}
      <ChatMessageList
        messages={messages}
        isLoading={isLoading}
      />

      {/* ── Suggestions — shown only when conversation is empty ──────────────── */}
      {!hasMessages && (
        <>
          <Divider />
          <ChatSuggestions
            userRoles={userRoles}
            onSelect={handleSuggestion}
          />
        </>
      )}

      {/* ── Send-level error banner ──────────────────────────────────────────── */}
      {error && (
        <Alert
          severity="error"
          sx={{ mx: 1.5, mb: 1, borderRadius: 2, fontSize: '0.8rem', py: 0.5 }}
        >
          {error}
        </Alert>
      )}

      {/* ── Input bar ───────────────────────────────────────────────────────── */}
      <ChatInputBar
        onSend={sendMessage}
        disabled={inputDisabled}
      />
    </Drawer>
  );
}
